import { Link } from "wouter";
import { ArrowRight, Monitor, KeyRound, MonitorSmartphone, Server, Laptop, Download } from "lucide-react";
import { SITE_FACTS } from "@shared/siteFacts";

const F = SITE_FACTS;

/** Real usage, said plainly: people believe the numbers we show them. */
export function ProofStrip() {
  const stats = [
    { v: F.usage.users, l: "people use it" },
    { v: F.usage.countries, l: "countries" },
    { v: F.usage.directPct, l: "come straight to the site" },
    { v: F.usage.languages, l: "languages" },
  ];
  return (
    <section className="container py-8 border-t border-border/40" aria-label="FreeDailyPro in numbers">
      <p className="text-center text-sm font-medium text-muted-foreground mb-4">More than {F.usage.users.replace("+", "")} people already work this way, in {F.usage.countries} countries.</p>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
        {stats.map((s) => (
          <div key={s.l} className="rounded-xl border border-border/60 bg-card p-4 text-center">
            <dt className="sr-only">{s.l}</dt>
            <dd className="text-2xl md:text-3xl font-extrabold tracking-tight text-primary">{s.v}</dd>
            <dd className="text-xs text-muted-foreground mt-1">{s.l}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** The deck's opening: name the pain, then show the better way. */
export function ProblemAndPromise() {
  const pains = [
    ["Scattered tools", "A different website for every PDF, image or invoice."],
    ["Subscriptions add up", "A paid plan on every site, and the cost keeps rising."],
    ["Your data, their servers", "Personal data and files go to servers you cannot vet."],
    ["Blocked at work", "Company AI policies shut many of these sites out."],
  ];
  const promises = [
    { icon: Monitor, t: "One site", d: "Every task, one address. No more hunting for a new website each time." },
    { icon: KeyRound, t: "No login wall", d: "Open a tool and start. Most tools need no sign-up at all." },
    { icon: MonitorSmartphone, t: "Any device", d: "Phone, tablet, laptop or desktop, the same way." },
  ];
  return (
    <section className="container py-14 border-t border-border/40">
      <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">The problem</p>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-5">Your work is scattered across a dozen websites, and each one wants your files.</h2>
          <ol className="space-y-3">
            {pains.map(([t, d], i) => (
              <li key={t} className="flex gap-3 rounded-xl border border-border/60 bg-card p-3.5">
                <span className="font-mono text-xs text-muted-foreground pt-0.5">0{i + 1}</span>
                <span><span className="font-semibold text-sm block">{t}</span><span className="text-sm text-muted-foreground">{d}</span></span>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">The better way</p>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-5 gradient-text">Open one site and just get the job done.</h2>
          <ul className="space-y-3">
            {promises.map((p) => (
              <li key={p.t} className="flex gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3.5">
                <p.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                <span><span className="font-semibold text-sm block">{p.t}</span><span className="text-sm text-muted-foreground">{p.d}</span></span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-muted-foreground">Start with one tool. Grow into full workflows, your own product, or a custom build, on the same platform.</p>
        </div>
      </div>
    </section>
  );
}

/** One key. Every API. */
export function ApiMcpBand() {
  return (
    <section className="container py-14 border-t border-border/40">
      <div className="max-w-5xl mx-auto rounded-2xl border border-primary/30 bg-primary/5 p-8 md:p-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">API &amp; MCP</p>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">One key. Every API.</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl">Call the same tools from your own code or AI agent. Find us on RapidAPI and Postman, and in the official MCP Registry.</p>
        <dl className="grid grid-cols-3 gap-3 max-w-xl mb-6">
          {[[F.apiCount, "APIs"], [F.mcpCount, "tools via MCP"], ["1", "key for all of them"]].map(([v, l]) => (
            <div key={l} className="rounded-xl bg-card border border-border/60 p-4 text-center">
              <dd className="text-3xl font-extrabold text-primary">{v}</dd>
              <dt className="text-xs text-muted-foreground mt-1">{l}</dt>
            </div>
          ))}
        </dl>
        <Link href="/developers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">Open the developer page <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
}

/** Coverage, and the promise to add what is missing. */
export function CoverageClaim() {
  return (
    <section className="container py-12 border-t border-border/40">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">World leader in coverage</p>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">Almost every professional request.</h2>
        <p className="text-muted-foreground mb-3">{F.worldLeaderLine}</p>
        <p className="text-muted-foreground mb-5">If something is missing, just ask and we will add it.</p>
        <Link href="/contact" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">Missing something? Ask us <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
}

/** Why IT can say yes. */
export function PrivacyFlow() {
  const steps = [
    { icon: Laptop, k: "On your device", t: "Open your file" },
    { icon: Monitor, k: "In your browser", t: "The work happens here" },
    { icon: Download, k: "Back to you", t: "Download the result" },
  ];
  return (
    <section className="container py-14 border-t border-border/40">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Trust</p>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Most tools run in your browser, so IT can say yes.</h2>
        <p className="text-muted-foreground mb-6">Privacy-first and browser-based; the few server-side tools are labeled.</p>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          {steps.map((s, i) => (
            <div key={s.k} className="relative rounded-xl border border-border/60 bg-card p-4">
              <s.icon className="h-5 w-5 text-primary mb-2" aria-hidden="true" />
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.k}</p>
              <p className="font-semibold text-sm">{s.t}</p>
              {i < 2 && <ArrowRight className="hidden sm:block absolute -right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-primary z-10" aria-hidden="true" />}
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4"><p className="text-3xl font-extrabold text-primary">100%</p><p className="text-xs text-muted-foreground">data protection</p></div>
          <div className="rounded-xl border border-border/60 bg-card p-4"><Server className="h-5 w-5 text-primary mb-1" aria-hidden="true" /><p className="text-xs uppercase tracking-wider text-muted-foreground">Our servers</p><p className="font-semibold text-sm">No file storage at all.</p></div>
        </div>
      </div>
    </section>
  );
}

/** You are never stuck: one slim row (the FAQ follows right below). */
export function SupportRow() {
  return (
    <section className="container py-8 border-t border-border/40">
      <p className="max-w-5xl mx-auto text-sm text-muted-foreground text-center">
        Every tool has a how-to guide, tips, FAQ and usage video. Still stuck? Use the feedback box on any tool, the <Link href="/contact" className="text-primary underline">Contact Us page</Link>, or write to <a className="text-primary underline" href={`mailto:${F.supportEmail}`}>{F.supportEmail}</a>.
      </p>
    </section>
  );
}
